import React from 'react';
import { cn } from '../../utils/cn';

interface DraggableHeaderProps<K extends string> {
  /** This column's stable key — moved as the drag payload. */
  colKey: K;
  /** Reorder callback fired when something is dropped onto this header. */
  onMove: (from: K, to: K) => void;
  /** Header content (typically the existing sort button). */
  children: React.ReactNode;
  /** Pass-through `<th>` className extras. */
  className?: string;
}

/**
 * `<th>` wrapper that adds HTML5 drag-and-drop reordering.
 *
 * - On drag start: writes its colKey into the dataTransfer payload.
 * - On drag over: shows a drop-target highlight.
 * - On drop: calls `onMove(fromKey, thisKey)`.
 *
 * Sort interactions inside `children` (typically a `<button>`) keep working
 * because clicks aren't drags. Mouse-down on the grip icon initiates the drag.
 */
export function DraggableHeader<K extends string>({
  colKey, onMove, children, className,
}: DraggableHeaderProps<K>) {
  const [isDragging, setIsDragging] = React.useState(false);
  const [isDropTarget, setIsDropTarget] = React.useState(false);

  return (
    <th
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', colKey);
        e.dataTransfer.effectAllowed = 'move';
        setIsDragging(true);
      }}
      onDragEnd={() => setIsDragging(false)}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes('text/plain')) {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          setIsDropTarget(true);
        }
      }}
      onDragLeave={() => setIsDropTarget(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDropTarget(false);
        const from = e.dataTransfer.getData('text/plain');
        if (from && from !== colKey) onMove(from as K, colKey);
      }}
      className={cn(
        'px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap select-none cursor-grab active:cursor-grabbing transition-colors',
        isDragging && 'opacity-40',
        isDropTarget && 'bg-blue-50 dark:bg-blue-900/20',
        className,
      )}
      title="Drag to reorder column"
    >
      {children}
    </th>
  );
}

export default DraggableHeader;
